import { useEffect, useMemo, useState } from "react"
import CreatableSelect from "react-select/creatable"
import { Button, Col, Input, Label, Modal, ModalBody, ModalHeader, Row } from "reactstrap"

import VehicleIdSelect from "components/Common/VehicleIdSelect"
import { formatCurrency } from "utils/formatCurrency"

const emptyItem = { payment_for_id: "", vehicle_id: "", amount: "", notes: "" }
const newEmptyItem = () => ({ payment_for_id: "", vehicle_id: "", amount: "", notes: "" })

const selectStyles = {
  control: (base, state) => Object.assign({}, base, {
    backgroundColor: state.isDisabled ? "#e9ecef" : "#fff",
    borderColor: state.isFocused ? "#80bdff" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 0.15rem rgba(0,123,255,0.25)" : "none",
    minHeight: "38px",
  }),
  menu: base => Object.assign({}, base, { zIndex: 9999 }),
  input: base => Object.assign({}, base, { backgroundColor: "transparent" }),
  singleValue: base => Object.assign({}, base, { backgroundColor: "transparent" }),
}

const toOptions = items =>
  items.map(item => ({
    label: item.name,
    value: item.id,
  }))

const getSelectedOption = (options, value) => {
  if (!value) return null
  if (typeof value === "object" && value.value) return value

  const option = options.find(opt => String(opt.value) === String(value))
  return option || { label: String(value), value }
}

const getBillItems = bill => {
  if (bill && Array.isArray(bill.items) && bill.items.length > 0) {
    return bill.items.map(item => ({
      payment_for_id: item.payment_for_id || item.expense_type_id || "",
      vehicle_id: item.vehicle_id
        ? {
            label: item.vehicle && (item.vehicle.stock_id || item.vehicle.full_chassis) || String(item.vehicle_id),
            value: item.vehicle_id,
            raw: item.vehicle,
          }
        : "",
      amount: item.amount != null ? item.amount : "",
      notes: item.notes || item.description || "",
    }))
  }

  return [{
    payment_for_id: bill && (bill.payment_for_id || bill.expense_type_id) || "",
    vehicle_id: bill && bill.vehicle_id || "",
    amount: bill && bill.amount != null ? bill.amount : "",
    notes: bill && bill.notes || "",
  }]
}

const EmployeeBillFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  editingBill = null,
  mine = false,
  employees = [],
  paymentPurposes = [],
  initialEmployeeId = "",
}) => {
  const [form, setForm] = useState({ employee_id: "", notes: "" })
  const [items, setItems] = useState([emptyItem])

  const employeeOptions = useMemo(() => toOptions(employees), [employees])
  const purposeOptions = useMemo(() => toOptions(paymentPurposes), [paymentPurposes])

  useEffect(() => {
    if (!isOpen) return

    if (editingBill) {
      setForm({
        employee_id: editingBill.employee_id || "",
        notes: editingBill.notes || "",
      })
      setItems(getBillItems(editingBill))
      return
    }

    setForm({ employee_id: initialEmployeeId || "", notes: "" })
    setItems([newEmptyItem()])
  }, [editingBill, initialEmployeeId, isOpen])

  const total = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    [items]
  )

  const setFormValue = (key, value) => setForm(prev => Object.assign({}, prev, { [key]: value }))
  const setItemValue = (index, key, value) =>
    setItems(prev => prev.map((item, itemIndex) => itemIndex === index ? Object.assign({}, item, { [key]: value }) : item))
  const addItem = () => setItems(prev => prev.concat([newEmptyItem()]))
  const removeItem = index => setItems(prev => prev.filter((_, itemIndex) => itemIndex !== index))

  const submit = () => {
    onSubmit({
      employee_id: mine ? undefined : form.employee_id,
      notes: form.notes,
      amount: total,
      payment_for_id: items[0] && items[0].payment_for_id || "",
      items: items.map(item => ({
        payment_for_id: item.payment_for_id,
        vehicle_id: ((item.vehicle_id && item.vehicle_id.value) || item.vehicle_id) || null,
        amount: item.amount,
        notes: item.notes,
      })),
    })
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{editingBill ? "Edit Employee Bill" : "Employee Bill Request"}</ModalHeader>
      <ModalBody>
        <Row className="g-3">
          {!mine && (
            <Col xs={12}>
              <Label>Employee</Label>
              <CreatableSelect
                value={getSelectedOption(employeeOptions, form.employee_id)}
                options={employeeOptions}
                isClearable
                isSearchable
                isDisabled={!!editingBill || loading}
                placeholder="Select or create employee"
                onChange={option => setFormValue("employee_id", option ? option.value : "")}
                onCreateOption={value => setFormValue("employee_id", value)}
                noOptionsMessage={() => "No employee found - press Enter to create"}
                styles={selectStyles}
              />
            </Col>
          )}

          <Col xs={12}>
            <Label>Bill Items</Label>
            <div className="d-flex flex-column gap-2">
              {items.map((item, index) => (
                <div key={index} className="border rounded p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="fw-semibold small">Item {index + 1}</span>
                    <Button color="danger" outline size="sm" disabled={items.length === 1 || loading} onClick={() => removeItem(index)}>
                      <i className="mdi mdi-delete-outline" />
                    </Button>
                  </div>
                  <Row className="g-2">
                    <Col md={6}>
                      <Label>Payment Purpose</Label>
                      <CreatableSelect
                        value={getSelectedOption(purposeOptions, item.payment_for_id)}
                        options={purposeOptions}
                        isClearable
                        isSearchable
                        isDisabled={loading}
                        placeholder="Select or create purpose"
                        onChange={option => setItemValue(index, "payment_for_id", option ? option.value : "")}
                        onCreateOption={value => setItemValue(index, "payment_for_id", value)}
                        noOptionsMessage={() => "No purpose found - press Enter to create"}
                        styles={selectStyles}
                      />
                    </Col>
                    <Col md={6}>
                      <Label>Amount</Label>
                      <Input type="number" min="0" step="0.01" value={item.amount} disabled={loading} onChange={e => setItemValue(index, "amount", e.target.value)} />
                    </Col>
                    <Col xs={12}>
                      <VehicleIdSelect
                        formik={{
                          values: { vehicle_id: item.vehicle_id },
                          setFieldValue: (_, value) => setItemValue(index, "vehicle_id", value),
                        }}
                      />
                    </Col>
                    <Col xs={12}>
                      <Label>Item Notes</Label>
                      <Input type="textarea" rows={2} value={item.notes} disabled={loading} onChange={e => setItemValue(index, "notes", e.target.value)} />
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
            <div className="text-end mt-2">
              <Button color="light" size="sm" onClick={addItem} disabled={loading}>
                <i className="mdi mdi-plus me-1" />Add Item
              </Button>
            </div>
            <div className="text-end fw-semibold mt-2">
              Total: {formatCurrency(total, "BDT")}
            </div>
          </Col>

          <Col xs={12}>
            <Label>Bill Notes</Label>
            <Input type="textarea" value={form.notes} disabled={loading} onChange={e => setFormValue("notes", e.target.value)} />
          </Col>
          <Col xs={12} className="text-end">
            <Button color="primary" disabled={loading} onClick={submit}>{editingBill ? "Update" : "Save"}</Button>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  )
}

export default EmployeeBillFormModal
